import { useState, useEffect } from "react";
import { Loader2, FileQuestion, CheckCircle2, XCircle, RotateCcw, Play } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const selCls = "bg-surface border border-hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-gold/40 text-ink";

export const PyqPractice = ({ user, onLimit, onGraded }) => {
  const [filters, setFilters] = useState({ exams: [], subjects: [], years: [], total: 0 });
  const [exam, setExam] = useState("");
  const [subject, setSubject] = useState("");
  const [year, setYear] = useState("");
  const [qtype, setQtype] = useState("mcq");
  const [count, setCount] = useState(10);
  const [stage, setStage] = useState("select"); // select | attempt | results
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [practiceId, setPracticeId] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/pyq/filters").then(({ data }) => {
      setFilters(data);
      if (data.exams?.length) setExam(data.exams[0]);
    }).catch(() => {});
  }, []);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/pyq/practice", { exam, subject: subject || null, year: year || null, qtype, count: Number(count) });
      setQuestions(data.questions);
      setPracticeId(data.practice_id);
      setAnswers({});
      setResults(null);
      setStage("attempt");
    } catch (e) {
      if (e.response?.status === 402) { onLimit(e.response.data?.detail?.reason === "free_limit" ? "free" : "guest"); return; }
      if (e.response?.status === 404) { toast.error(e.response.data?.detail?.message || "No questions found."); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/pyq/grade", { practice_id: practiceId, answers });
      setResults(data);
      setStage("results");
      onGraded?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setStage("select"); setQuestions([]); setResults(null); setAnswers({}); };

  return (
    <div className="glass-card rounded-3xl p-7" data-testid="prep-pyq">
      <div className="flex items-center gap-2">
        <FileQuestion className="w-5 h-5 text-gold" />
        <h3 className="font-serif text-2xl text-ink">Previous Year Questions</h3>
      </div>
      <p className="text-sm text-inkfaint mt-1">
        {filters.total > 0 ? `${filters.total} questions in the bank · attempt & get instant scoring.` : "Practice PYQ-style questions with instant scoring."}
      </p>

      {/* Select stage */}
      {stage === "select" && (
        filters.total === 0 ? (
          <p className="text-sm text-inkmuted mt-6 border border-dashed border-hairline rounded-2xl p-6 text-center" data-testid="pyq-empty">
            No questions added yet. An admin can add real PYQs or AI-generate practice sets from the Admin Dashboard.
          </p>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <select className={selCls} value={exam} onChange={(e) => setExam(e.target.value)} data-testid="pyq-exam-select">
                {filters.exams.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <select className={selCls} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="pyq-subject-select">
                <option value="">All subjects</option>
                {filters.subjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className={selCls} value={year} onChange={(e) => setYear(e.target.value)} data-testid="pyq-year-select">
                <option value="">All years</option>
                {filters.years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className={selCls} value={qtype} onChange={(e) => setQtype(e.target.value)} data-testid="pyq-type-select">
                <option value="mcq">MCQ</option>
                <option value="descriptive">Descriptive</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-inkmuted">Questions:</label>
              <select className={selCls} value={count} onChange={(e) => setCount(e.target.value)} data-testid="pyq-count-select">
                {[5, 10, 15, 20].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button onClick={start} disabled={loading || !exam} data-testid="pyq-start-btn"
                className="ml-auto rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-50 px-6 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all flex items-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Start Practice
              </button>
            </div>
          </div>
        )
      )}

      {/* Attempt stage */}
      {stage === "attempt" && (
        <div className="mt-6 space-y-5" data-testid="pyq-attempt">
          {questions.map((q, i) => (
            <div key={q.id} className="glass-card rounded-2xl p-5">
              <p className="text-sm text-ink font-medium">{i + 1}. {q.question}</p>
              {q.qtype === "mcq" ? (
                <div className="space-y-2 mt-3">
                  {q.options.map((opt) => {
                    const letter = opt.trim()[0]?.toUpperCase();
                    const selected = answers[q.id] === letter;
                    return (
                      <button key={opt} onClick={() => setAnswers((a) => ({ ...a, [q.id]: letter }))}
                        data-testid={`pyq-opt-${q.id}-${letter}`}
                        className={`w-full text-left text-sm rounded-xl px-4 py-2.5 border transition-all ${selected ? "border-gold bg-gold/10 text-ink" : "border-hairline text-inkmuted hover:border-hairline"}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <textarea className="w-full mt-3 bg-surface border border-hairline rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/40 text-ink" rows={3}
                  placeholder="Write your answer…" value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} data-testid={`pyq-desc-${q.id}`} />
              )}
            </div>
          ))}
          <div className="flex gap-3">
            <button onClick={reset} className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider">Cancel</button>
            <button onClick={submit} disabled={loading} data-testid="pyq-submit-btn"
              className="ml-auto rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-50 px-6 py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit &amp; Score
            </button>
          </div>
        </div>
      )}

      {/* Results stage */}
      {stage === "results" && results && (
        <div className="mt-6 space-y-5" data-testid="pyq-results">
          {results.score_pct !== null && (
            <div className="text-center glass-card rounded-2xl p-6" data-testid="pyq-score">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-inkfaint">Your Score</p>
              <p className="font-serif text-5xl gold-text mt-2">{results.score_pct}%</p>
              <p className="text-sm text-inkmuted mt-1">{results.mcq_correct} of {results.mcq_total} MCQs correct</p>
            </div>
          )}
          {results.results.map((r, i) => (
            <div key={r.id} className="glass-card rounded-2xl p-5">
              <div className="flex items-start gap-2">
                {r.is_correct === true && <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />}
                {r.is_correct === false && <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />}
                <p className="text-sm text-ink font-medium">{i + 1}. {r.question}</p>
              </div>
              <div className="mt-3 text-sm space-y-1 pl-7">
                {r.qtype === "mcq" ? (
                  <>
                    <p className="text-inkmuted">Your answer: <span className={r.is_correct ? "text-emerald-400" : "text-red-400"}>{r.your_answer || "—"}</span></p>
                    <p className="text-inkmuted">Correct answer: <span className="text-gold">{r.correct_answer}</span></p>
                  </>
                ) : (
                  <p className="text-inkmuted">Model answer: <span className="text-ink">{r.correct_answer}</span></p>
                )}
                {r.explanation && <p className="text-inkfaint italic mt-1">{r.explanation}</p>}
              </div>
            </div>
          ))}
          <button onClick={reset} data-testid="pyq-again-btn"
            className="rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> Practice Again
          </button>
        </div>
      )}
    </div>
  );
};
